import * as React from "react";
import SwipeableViews from "react-swipeable-views";
import { useTheme } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Div from "@jumbo/shared/Div";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
} from "@mui/material";
import PricingPlan from "./PricingPlan";
import List from "@mui/material/List";
import { useAppMutation } from "../../../services";
import parentServices from "../../../services/parent-services";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Payment from "./Payment";
import BoostPayment from "./BoostPayment";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BoostInfoModal from "./boostInfo";

// ---------- TabPanel ----------
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Div sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Div>
      )}
    </div>
  );
};

const a11yProps = (index) => ({
  id: `full-width-tab-${index}`,
  "aria-controls": `full-width-tabpanel-${index}`,
});

// ---------- helpers ----------
const fmt = (iso) => (iso ? moment(iso).format("DD/MM/YYYY") : "-");

const isExpiredByDate = (iso) => {
  if (!iso) return false;
  return new Date(iso) < new Date();
};

const FullWidthTabs = () => {
  const params = useParams();
  const theme = useTheme();
  const [value, setValue] = React.useState(1);
  const { t } = useTranslation();

  const [selectedSubscription, setSelectedSubscription] = React.useState({});
  const [cancelReason, setCancelReason] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogSubscriptionId, setDialogSubscriptionId] = React.useState(null);
  const [errCancel, setErrCancel] = React.useState("");
  const [expandedIndex, setExpandedIndex] = React.useState(-1);

  const [selectedBoost, setSelectedBoost] = React.useState(null);

  const hideBoostPayment = () => setSelectedBoost(null);

  // Boost modal
  const [boostInfoOpen, setBoostInfoOpen] = React.useState(false);

  const {
    mutate: loadSubscriptions,
    data: sData,
    isSuccess: isSSuccess,
    isLoading: isSLoading,
  } = useAppMutation(parentServices.loadSubscriptions);

  const {
    mutate: cancelSubscription,
    isError: isCError,
    isSuccess: isCSuccess,
    isLoading: isCLoading,
  } = useAppMutation(parentServices.cancelSubscription);

  // IMPORTANT: new API shape => sData.keySubs
  const keySubs = sData?.keySubs || [];

  const hideSubscription = () => setSelectedSubscription({});

  const handleChange = (event, newValue) => setValue(newValue);
  const handleChangeIndex = (index) => setValue(index);

  React.useEffect(() => {
    loadSubscriptions({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only refresh after cancel succeeds
  React.useEffect(() => {
    if (isCSuccess) {
      loadSubscriptions({});
      setDialogOpen(false);
      setCancelReason("");
      setErrCancel("");
    }
    if (isCError) {
      setDialogOpen(false);
      setCancelReason("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCSuccess, isCError]);

  React.useEffect(() => {
    if (params?.category === "plans") setValue(1);
  }, [params]);

    React.useEffect(() => {
        // supported: subscribed | plans
        if (params?.category === "subscribed") setValue(0);
        else if (params?.category === "plans") setValue(1);
        // else keep default
    }, [params?.category]);


    const handleCancelSubscription = (subscriptionId) => {
    setDialogSubscriptionId(subscriptionId);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCancelReason("");
    setErrCancel("");
  };

  const handleDialogConfirm = () => {
    if (cancelReason?.trim()) {
      cancelSubscription({
        subscriptionId: dialogSubscriptionId,
        cancelReason: cancelReason.trim(),
      });
    } else {
      setErrCancel(t("parent-subscription.title.cancelReasonRequired"));
    }
  };

  return (
    <Card
      sx={{
        display: "flex",
        minWidth: 0,
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          direction: "column",
          p: 2,
          alignItems: "center",
          alignContent: "center",
          justifyContent: "center",
          marginLeft: -1,
          marginRight: -1,
        }}
      >
        <Payment subscription={selectedSubscription} hide={hideSubscription} />

        <BoostPayment subscription={selectedBoost} hide={hideBoostPayment} />
        <Div sx={{ maxWidth: "100%", bgcolor: "background.paper" }}>
          <AppBar position="static">
            <Tabs
              value={value}
              onChange={handleChange}
              indicatorColor="secondary"
              textColor="inherit"
              variant="fullWidth"
              aria-label="full width tabs example"
            >
              <Tab
                label={t("parent-subscription.title.subscribed")}
                {...a11yProps(0)}
              />
              <Tab
                label={t("parent-subscription.title.plan")}
                {...a11yProps(1)}
              />
            </Tabs>
          </AppBar>

          <SwipeableViews
            style={{ marginRight: -20, marginLeft: -20 }}
            axis={theme.direction === "rtl" ? "x-reverse" : "x"}
            index={value}
            onChangeIndex={handleChangeIndex}
          >
            {/* ---------------------- TAB 0: SUBSCRIBED ---------------------- */}
            <TabPanel value={value} index={0} dir={theme.direction}>
              <List dense sx={{ width: "100%", bgcolor: "background.paper" }}>
                {/* ---------------------- BOOST BANNER (RESPONSIVE) ---------------------- */}
                <Paper
                  elevation={0}
                  sx={{
                    mb: 3,
                    backgroundImage:
                      "linear-gradient(135deg, rgb(255, 249, 230) 0%, rgb(255, 224, 130) 100%)",
                    border: "1.33333px solid rgb(255, 193, 7)",
                    borderRadius: "12px",
                    boxSizing: "border-box",

                    // Responsive layout
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 1.2, sm: 0 },
                    px: { xs: 2, sm: "25px" },
                    py: { xs: 2, sm: "20px" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "center", sm: "center" },
                      justifyContent: { xs: "center", sm: "flex-start" },
                      gap: "15px",
                      flex: 1,
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "28px", sm: "35px" },
                        lineHeight: 1,
                      }}
                    >
                      ⚡
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "rgb(245, 124, 0)",
                          fontFamily: "Nunito, sans-serif",
                          fontSize: { xs: "14px", sm: "18px" },
                          fontWeight: 700,
                          lineHeight: 1.2,
                        }}
                      >
                        {t("parent-subscription.title.newBoost")}
                      </Typography>

                      <Typography
                        sx={{
                          color: "rgb(93, 64, 55)",
                          fontFamily: "Nunito, sans-serif",
                          fontSize: { xs: "12px", sm: "14px" },
                          fontWeight: 400,
                          mt: 0.6,
                          lineHeight: 1.35,
                        }}
                      >
                        {t("parent-subscription.title.newBoostSub")}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    disableElevation
                    onClick={() => setBoostInfoOpen(true)}
                    fullWidth={true}
                    sx={{
                      mt: { xs: 1.4, sm: 0 },
                      height: { xs: 36, sm: 35 },
                      px: "16px",
                      borderRadius: "8px",
                      backgroundColor: "rgb(29, 106, 222)",
                      color: "#fff",
                      fontFamily: "Nunito, sans-serif",
                      fontSize: "13px",
                      fontWeight: 700,
                      textTransform: "none",
                      width: { xs: "100%", sm: "auto" },
                      alignSelf: { xs: "stretch", sm: "center" },
                      "&:hover": { backgroundColor: "rgb(30, 64, 175)" },
                    }}
                  >
                    {t("parent-subscription.title.learnMore")}
                  </Button>
                </Paper>

                {/* ---------------------- keySubs rendering ---------------------- */}
                {keySubs.map((group, index) => {
                  console.log("Full Data ------->>", keySubs);
                  const sub = group?.subscription || {};
                  const plan = sub?.plan || {};
                  const category = plan?.category; // "subscription" | "packs"
                  const isPack = category === "packs";
                  const isSubscription = category === "subscription";
                  const isCancelled = Boolean(sub?.cancelled);

                  const expired = isExpiredByDate(sub?.expiredDate);
                  const hasHistory =
                    Array.isArray(group?.subscriptions) &&
                    group.subscriptions.length > 0;

                  // TEMP: hardcode boost (per your request)
                  const isBoost = Boolean(sub?.boast?.subscription);

                  let stripColor = isPack
                    ? "rgb(76, 175, 80)" // packs → green
                    : isSubscription && isBoost
                      ? "rgb(255, 193, 7)" // boost → yellow
                      : "rgb(76, 175, 80)"; // normal → green

                  // ✅ highest priority states
                  if (expired || isCancelled) {
                    stripColor = "rgb(158, 158, 158)";
                  }

                  // ---- PACK UI (requested)
                  const packSubtitle = `Pack ${sub?.noOfBooking ?? 0} ${t(
                    "parent-subscription.title.course",
                  )} (€${Number(sub?.chargedAmount ?? sub?.amount ?? 0).toFixed(2)})`;

                  const purchaseDate = fmt(sub?.startDate);
                  const expiryDate = fmt(sub?.expiredDate);
                  const lessonsUsed = `${sub?.noOfUsedBooking ?? 0}/${sub?.noOfBooking ?? 0}`;

                  // ---- SUBSCRIPTION subtitle
                  const subtitle = isSubscription
                    ? t("common.subscriptionTitle")
                        .replace("?", sub?.noOfBooking)
                        .concat(
                          ` (€${Number(sub?.chargedAmount ?? sub?.amount ?? 0).toFixed(2)}/${t(
                            "parent-subscription.title.month",
                          )})`,
                        )
                    : "";

                  const nextPayment = isSubscription
                    ? fmt(sub?.nextPaymentDate)
                    : "-";

                  const isExpanded = expandedIndex === index;

                  const canShowCancelBtn =
                    Boolean(sub?.allowCancellation) &&
                    !sub?.cancelled &&
                    !expired;

                  // ---------- Summary Content (PACK or SUBSCRIPTION) ----------
                  const SummaryContent = (
                    <Box sx={{ width: "100%" }}>
                      {/* PACK (same for all screens) */}
                      {isPack ? (
                        <Box>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography
                              sx={{
                                color: "rgb(0, 36, 101)",
                                fontFamily: "Nunito, sans-serif",
                                fontSize: { xs: "16px", sm: "20px" },
                                fontWeight: 800,
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {plan?.name || "-"}
                            </Typography>
                          </Stack>

                          <Typography
                            sx={{
                              mt: 1.5,
                              color: "rgb(102, 102, 102)",
                              fontFamily: "Nunito, sans-serif",
                              fontSize: { xs: "13px", sm: "16px" },
                              display: "block",
                              lineHeight: 1.4,
                            }}
                          >
                            {packSubtitle}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 1.0,
                              fontSize: 12.5,
                              color: "rgba(0,0,0,0.78)",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: "rgb(0, 36, 101)",
                                fontFamily: "Nunito, sans-serif",
                                fontSize: "13px",
                                fontWeight: 700,
                                display: "inline",
                              }}
                            >
                              {t("parent-subscription.title.purchaseDate")}
                            </Box>{" "}
                            {purchaseDate}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.35,
                              fontSize: 12.5,
                              color: "rgba(0,0,0,0.78)",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: "rgb(0, 36, 101)",
                                fontFamily: "Nunito, sans-serif",
                                fontSize: "13px",
                                fontWeight: 700,
                                display: "inline",
                              }}
                            >
                              {t("parent-subscription.title.expiryDate")}
                            </Box>{" "}
                            {expiryDate}
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.35,
                              fontSize: 12.5,
                              color: "rgba(0,0,0,0.78)",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: "rgb(0, 36, 101)",
                                fontFamily: "Nunito, sans-serif",
                                fontSize: "13px",
                                fontWeight: 700,
                                display: "inline",
                              }}
                            >
                              {t("parent-subscription.title.lessonUsed")}
                            </Box>{" "}
                            {lessonsUsed}
                          </Typography>

                          {/*<Typography
                            sx={{
                              mt: 0.6,
                              color: "rgb(153, 153, 153)",
                              fontFamily: "Nunito, sans-serif",
                              fontSize: "13px",
                              display: "block",
                              lineHeight: 1.4,
                            }}
                          >
                            ID: {sub?.subscriptionId || sub?._id || "-"}
                          </Typography>*/}
                        </Box>
                      ) : null}

                      {/* SUBSCRIPTION: MOBILE like your screenshot + DESKTOP keep your layout */}
                      {isSubscription ? (
                        <>
                          {/* MOBILE (xs) */}
                          <Box sx={{ display: { xs: "block", sm: "none" } }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{
                                minWidth: 0,
                                flexWrap: "wrap", // ✅ only wraps when needed
                                rowGap: 0.6, // ✅ spacing when it wraps to next line
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "rgb(0, 36, 101)",
                                  fontFamily: "Nunito, sans-serif",
                                  fontSize: "15px",
                                  fontWeight: 800,
                                  lineHeight: 1.2,
                                  cursor: "pointer",

                                  minWidth: 0,
                                  flex: "1 1 auto", // ✅ name takes available space
                                  whiteSpace: "nowrap", // ✅ keep in one line
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {plan?.name || "-"}
                              </Typography>

                              {isSubscription && isBoost && (
                                <Stack
                                  direction="row"
                                  spacing={0.6}
                                  alignItems="center"
                                  sx={{
                                    flex: "0 0 auto", // ✅ keep size, wrap to next line only if needed
                                    whiteSpace: "nowrap", // ✅ don't break inside boost label
                                  }}
                                >
                                  <Box sx={{ mr: 0.4, lineHeight: 1 }}>🚀</Box>

                                  <Typography
                                    sx={{
                                      color: "rgb(255, 137, 51)",
                                      fontFamily: "Nunito, sans-serif",
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      letterSpacing: 0.2,
                                      lineHeight: 1.2,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {t("parent-subscription.title.boostActive")}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>

                            <Typography
                              sx={{
                                color: "rgb(102, 102, 102)",
                                fontFamily: "Nunito, sans-serif",
                                fontSize: "12px",
                                lineHeight: 1.4,
                                mb: 0.8,
                                mt: 1,
                              }}
                            >
                              {subtitle}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: "rgba(0,0,0,0.78)",
                                mb: 0.4,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: "rgb(0, 36, 101)",
                                  fontFamily: "Nunito, sans-serif",
                                  fontSize: "11.5px",
                                  fontWeight: 800,
                                }}
                              >
                                {t("parent-subscription.title.nextPayment")}:
                              </Box>{" "}
                              {nextPayment}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: "rgba(0,0,0,0.78)",
                                mb: 0.4,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: "rgb(0, 36, 101)",
                                  fontFamily: "Nunito, sans-serif",
                                  fontSize: "11.5px",
                                  fontWeight: 800,
                                }}
                              >
                                {t(
                                  "parent-subscription.title.subscriptionDate",
                                )}
                                :
                              </Box>{" "}
                              {fmt(sub?.commitmentStartDate)}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: "rgba(0,0,0,0.78)",
                                mb: 0.6,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  color: "rgb(0, 36, 101)",
                                  fontFamily: "Nunito, sans-serif",
                                  fontSize: "11.5px",
                                  fontWeight: 800,
                                }}
                              >
                                {t("parent-subscription.title.commitmentDate")}:
                              </Box>{" "}
                              {fmt(sub?.commitmentEndDate)}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: "rgba(0,0,0,0.65)",
                                mb: 1.2,
                                wordBreak: "break-all",
                              }}
                            >
                              ID: {sub?.subscriptionId || sub?._id || "-"}
                            </Typography>

                            <Stack spacing={1}>
                              <Chip
                                label={
                                  sub?.cancelled
                                    ? t(
                                        "parent-subscription.title.cancelled",
                                      ) || "Cancelled"
                                    : expired
                                      ? t("parent-subscription.title.expired")
                                      : t("parent-subscription.title.active")
                                }
                                size="small"
                                sx={{
                                  alignSelf: "flex-start",
                                  height: 28,
                                  fontFamily: "Nunito, sans-serif",
                                  borderRadius: "999px",
                                  fontWeight: 800,
                                  fontSize: "12px",
                                  bgcolor:
                                    sub?.cancelled || expired
                                      ? "rgb(224, 224, 224)"
                                      : "rgb(76, 175, 80)",
                                  color:
                                    sub?.cancelled || expired
                                      ? "rgb(153, 153, 153)"
                                      : "#FFF",
                                }}
                              />

                              {canShowCancelBtn ? (
                                <Button
                                  variant="outlined"
                                  onClick={() =>
                                    handleCancelSubscription(
                                      sub?.subscriptionId,
                                    )
                                  }
                                  disabled={isCLoading}
                                  fullWidth
                                  sx={{
                                    height: 36,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontFamily: "Nunito, sans-serif",
                                    fontWeight: 800,
                                    fontSize: "12px",
                                    borderColor: "rgb(29, 106, 222)",
                                    color: "rgb(29, 106, 222)",
                                    bgcolor: "#fff",
                                    "&:hover": {
                                      borderColor: "rgb(29, 106, 222)",
                                      bgcolor: "rgba(29,78,216,0.04)",
                                    },
                                  }}
                                >
                                  {t("parent-subscription.title.cancelBtn")}
                                </Button>
                              ) : null}

                              {isSubscription && !sub?.paid ? (
                                <Button
                                  variant="outlined"
                                  onClick={() => setSelectedSubscription(sub)}
                                  sx={{
                                    height: 32,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    px: 2.2,
                                    borderColor: "#DC2626",
                                    color: "#DC2626",
                                    bgcolor: "#fff",
                                    "&:hover": {
                                      borderColor: "#B91C1C",
                                      bgcolor: "rgba(220,38,38,0.05)",
                                    },
                                  }}
                                >
                                  {t("parent-subscription.title.pay")}
                                </Button>
                              ) : null}

                              {isSubscription &&
                              !expired &&
                              !sub?.cancelled &&
                              !isBoost ? (
                                <Button
                                  disableElevation
                                  sx={{
                                    height: 40,
                                    px: 2.5,
                                    borderRadius: "10px",
                                    backgroundColor: "#FFD600",
                                    color: "#000",
                                    fontFamily: "Nunito, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    textTransform: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.8,
                                    "&:hover": {
                                      backgroundColor: "#FFCC00",
                                    },
                                  }}
                                  onClick={() => setSelectedBoost(sub)}
                                >
                                  <Box
                                    component="span"
                                    sx={{ fontSize: 14, lineHeight: 1 }}
                                  >
                                    ⚡
                                  </Box>
                                  {t("parent-subscription.title.activateBoost")}
                                </Button>
                              ) : null}
                            </Stack>
                          </Box>

                          {/* DESKTOP (sm+) - keep your existing left+right layout */}
                          <Box sx={{ display: { xs: "none", sm: "block" } }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "stretch",
                                justifyContent: "space-between",
                                gap: 2,
                              }}
                            >
                              {/* Left side */}
                              <Box sx={{ minWidth: 0 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  <Typography
                                    sx={{
                                      color: "rgb(0, 36, 101)",
                                      fontFamily: "Nunito, sans-serif",
                                      fontSize: "20px",
                                      fontWeight: 800,
                                      cursor: "pointer",
                                      display: "block",
                                      lineHeight: 1.2,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {plan?.name || "-"}
                                  </Typography>

                                  {isSubscription && isBoost ? (
                                    <Stack
                                      direction="row"
                                      spacing={0.6}
                                      alignItems="center"
                                    >
                                      <Box sx={{ mr: 0.4 }}>🚀</Box>
                                      <Typography
                                        sx={{
                                          color: "rgb(255, 137, 51)",
                                          fontFamily: "Nunito, sans-serif",
                                          fontSize: "14px",
                                          fontWeight: 700,
                                          cursor: "pointer",
                                          display: "block",
                                          letterSpacing: 0.2,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {t(
                                          "parent-subscription.title.boostActive",
                                        )}
                                      </Typography>
                                    </Stack>
                                  ) : null}
                                </Stack>

                                <Typography
                                  sx={{
                                    mt: 1.5,
                                    color: "rgb(102, 102, 102)",
                                    fontFamily: "Nunito, sans-serif",
                                    fontSize: "16px",
                                    display: "block",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {subtitle}
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 1.0,
                                    fontSize: 12.5,
                                    color: "rgba(0,0,0,0.78)",
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "rgb(0, 36, 101)",
                                      fontFamily: "Nunito, sans-serif",
                                      fontSize: "14px",
                                      fontWeight: 700,
                                      display: "inline",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {t("parent-subscription.title.nextPayment")}
                                    :
                                  </Box>{" "}
                                  {nextPayment}
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 1,
                                    color: "rgb(153, 153, 153)",
                                    fontFamily: "Nunito, sans-serif",
                                    fontSize: "13px",
                                    display: "block",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {t(
                                    "parent-subscription.title.subscriptionDate",
                                  )}
                                  : {fmt(sub?.commitmentStartDate)}
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 0.35,
                                    color: "rgb(153, 153, 153)",
                                    fontFamily: "Nunito, sans-serif",
                                    fontSize: "13px",
                                    display: "block",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {t(
                                    "parent-subscription.title.commitmentDate",
                                  )}
                                  : {fmt(sub?.commitmentEndDate)}
                                </Typography>

                                <Typography
                                  sx={{
                                    mt: 0.35,
                                    color: "rgb(153, 153, 153)",
                                    fontFamily: "Nunito, sans-serif",
                                    fontSize: "13px",
                                    display: "block",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  ID: {sub?.subscriptionId || sub?._id || "-"}
                                </Typography>
                              </Box>

                              {/* Right side (your existing actions) */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Stack alignItems="flex-end" spacing={1}>
                                  <Chip
                                    label={
                                      sub?.cancelled
                                        ? t(
                                            "parent-subscription.title.cancelled",
                                          ) || "Cancelled"
                                        : expired
                                          ? t(
                                              "parent-subscription.title.expired",
                                            )
                                          : t(
                                              "parent-subscription.title.active",
                                            )
                                    }
                                    size="small"
                                    sx={{
                                      height: 35,
                                      width: 80,
                                      fontFamily: "Nunito, sans-serif",
                                      borderRadius: "999px",
                                      fontWeight: 700,
                                      fontSize: "13px",
                                      bgcolor:
                                        sub?.cancelled || expired
                                          ? "rgb(224, 224, 224)"
                                          : "rgb(76, 175, 80)",
                                      color:
                                        sub?.cancelled || expired
                                          ? "rgb(153, 153, 153)"
                                          : "#FFF",
                                    }}
                                  />

                                  {canShowCancelBtn ? (
                                    <Button
                                      variant="outlined"
                                      onClick={() =>
                                        handleCancelSubscription(
                                          sub?.subscriptionId,
                                        )
                                      }
                                      disabled={isCLoading}
                                      sx={{
                                        height: 40,
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        fontFamily: "Nunito, sans-serif",
                                        fontWeight: 700,
                                        fontSize: "13px",
                                        px: 2.2,
                                        borderColor: "rgb(29, 106, 222)",
                                        color: "rgb(29, 106, 222)",
                                        bgcolor: "#fff",
                                        "&:hover": {
                                          borderColor: "rgb(29, 106, 222)",
                                          bgcolor: "rgba(29,78,216,0.04)",
                                        },
                                      }}
                                    >
                                      {t("parent-subscription.title.cancelBtn")}
                                    </Button>
                                  ) : null}

                                  {isSubscription && !sub?.paid ? (
                                    <Button
                                      variant="outlined"
                                      onClick={() =>
                                        setSelectedSubscription(sub)
                                      }
                                      sx={{
                                        height: 32,
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        px: 2.2,
                                        borderColor: "#DC2626",
                                        color: "#DC2626",
                                        bgcolor: "#fff",
                                        "&:hover": {
                                          borderColor: "#B91C1C",
                                          bgcolor: "rgba(220,38,38,0.05)",
                                        },
                                      }}
                                    >
                                      {t("parent-subscription.title.pay")}
                                    </Button>
                                  ) : null}

                                  {isSubscription &&
                                  !expired &&
                                  !sub?.cancelled &&
                                  !isBoost ? (
                                    <Button
                                      disableElevation
                                      sx={{
                                        height: 40,
                                        px: 2.5,
                                        borderRadius: "10px",
                                        backgroundColor: "#FFD600",
                                        color: "#000",
                                        fontFamily: "Nunito, sans-serif",
                                        fontSize: "14px",
                                        fontWeight: 700,
                                        textTransform: "none",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.8,
                                        "&:hover": {
                                          backgroundColor: "#FFCC00",
                                        },
                                      }}
                                      onClick={() => setSelectedBoost(sub)}
                                    >
                                      <Box
                                        component="span"
                                        sx={{ fontSize: 14, lineHeight: 1 }}
                                      >
                                        ⚡
                                      </Box>
                                      {t(
                                        "parent-subscription.title.activateBoost",
                                      )}
                                    </Button>
                                  ) : null}
                                </Stack>
                              </Box>
                            </Box>
                          </Box>
                        </>
                      ) : null}
                    </Box>
                  );

                  // ---------- PACK: NO ACCORDION / NO EXPAND ICON ----------
                  if (isPack || !hasHistory) {
                    return (
                      <Paper
                        key={group?.key || `key-sub-${index}`}
                        elevation={0}
                        sx={{
                          position: "relative",
                          borderRadius: "10px",
                          border: "1px solid rgba(0,0,0,0.08)",
                          overflow: "hidden",
                          bgcolor: "#FFFFFF",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 6,
                            bgcolor: stripColor,
                          }}
                        />
                        <Box sx={{ px: 2, py: 1.8, pl: 3 }}>
                          {SummaryContent}
                        </Box>
                      </Paper>
                    );
                  }

                  // ---------- SUBSCRIPTION: ACCORDION WITH HISTORY ----------
                  return (
                    <Paper
                      key={group?.key || `key-sub-${index}`}
                      elevation={0}
                      sx={{
                        position: "relative",
                        borderRadius: "10px",
                        border: "1px solid rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        bgcolor: "#FFFFFF",
                        mb: 2,
                      }}
                    >
                      <Accordion
                        expanded={isExpanded}
                        onChange={() =>
                          setExpandedIndex(isExpanded ? -1 : index)
                        }
                        disableGutters
                        elevation={0}
                        sx={{
                          // ✅ boost highlight background
                          background: isBoost
                            ? "linear-gradient(90deg, rgb(255, 249, 230) 0%, rgba(255, 249, 230, 0) 100%)"
                            : "transparent",

                          // keep your existing styling
                          "&:before": { display: "none" },

                          // optional: ensure it doesn’t add default background/box
                          boxShadow: "none",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <PlayArrowIcon
                              sx={{ fontSize: 16, color: "rgba(0,0,0,0.55)" }}
                            />
                          }
                          sx={{
                            position: "relative",
                            px: 2,
                            py: { xs: 1.4, sm: 1.8 },
                            minHeight: { xs: 74, sm: 88 },
                            pl: 3,
                            "& .MuiAccordionSummary-content": {
                              my: 0,
                              alignItems: "stretch",
                            },
                            "& .MuiAccordionSummary-expandIconWrapper": {
                              order: -1,
                              mr: 1.2,
                              ml: 0,
                              alignSelf: "flex-start",
                              marginTop: { xs: "3px", sm: "2px" },
                              transform: isExpanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                              transition: "transform .18s ease",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 6,
                              bgcolor: stripColor,
                            }}
                          />
                          {SummaryContent}
                        </AccordionSummary>

                        <AccordionDetails
                          sx={{
                            px: 2,
                            pt: 2,
                            pb: 2,
                            backgroundColor: "#FFF",
                          }}
                        >
                          <Stack spacing={2} sx={{ pl: 3.2 }}>
                            {group.subscriptions.map((h, i) => {
                              //console.log("🔎 history item:", i, h);

                              //const isBoost = Boolean(h?.boast?.subscription) && i===0;

                                const result = (() => {
                                    const subs = group?.subscriptions.filter(item => !!item?.boast?.subscription) || [];

                                    // Step 1: check if any item has boast.subscription
                                    console.log('subs.length',subs.length)
                                    if (subs.length < 1) return null;

                                    // Step 2: return second last
                                    return subs[subs.length - 1];
                                })();

                               const isBoost =  sub?.boast?.subscription === h.subscriptionId

                                if(result){
                                    console.log('result------------>',result?.subscriptionId === h?.subscriptionId,result, h)
                                }
                               // const isBoost =  result?.subscriptionId === h.subscriptionId

                                const isBoostDate =  sub?.boast?.startDate


                                const paid = Boolean(h?.paid);
                              //const bg = paid ? "#FFFFFF" : "#FBE7EA";

                              const bg = !paid
                                ? "#FBE7EA" // unpaid / failed
                                : isBoost
                                  ? "rgb(255, 249, 230)" // ⚡ boost
                                  : "#FFFFFF"; // normal paid

                              const totalBookings = Number(h?.noOfBooking ?? 0);
                              const usedBookings = Number(
                                h?.noOfUsedBooking ?? 0,
                              );

                                const expired = isExpiredByDate(h?.expiredDate);


                                const allUsed =
                                paid &&
                                totalBookings > 0 &&
                                usedBookings >= totalBookings;

                              const stripOld = !paid
                                ? "#FF1E1E" // failed / unpaid (highest priority)
                                : isBoost
                                  ? "rgb(255, 193, 7)" // ⚡ boost → yellow
                                  : allUsed
                                    ? "rgb(224, 224, 224)" // paid but fully used
                                    : "rgb(76,175,80)"; // paid & active

                                const strip = !paid
                                    ? "#FF1E1E" // failed / unpaid
                                    : expired || allUsed
                                        ? "rgb(224, 224, 224)" // expired or fully used
                                        : isBoost
                                            ? "rgb(255, 193, 7)" // boost
                                            : "rgb(76,175,80)"; // paid & active

                              //const strip = paid ? "rgb(76,175,80)" : "#FF1E1E";
                              const statusColor = paid
                                ? "rgb(76,175,80)"
                                : "rgb(244,67,54)";
                              const titleIcon = paid ? "✓" : "✖";

                              const historyAmount = `€${Number(
                                h?.chargedAmount ?? h?.amount ?? 0,
                              ).toFixed(2)}`;

                              const historyLessons =
                                typeof h?.noOfBooking !== "undefined"
                                  ? `${h?.noOfUsedBooking ?? 0}/${h?.noOfBooking ?? 0}`
                                  : null;

                              return (
                                <Paper
                                  key={i}
                                  elevation={0}
                                  sx={{
                                    position: "relative",
                                    borderRadius: "10px",
                                    border: "1px solid rgba(0,0,0,0.08)",
                                    overflow: "hidden",
                                    bgcolor: bg,
                                    px: 3,
                                    py: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: 3,
                                      bgcolor: strip,
                                    }}
                                  />

                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      justifyContent: "space-between",
                                      gap: 2,
                                      flexDirection: {
                                        xs: "column",
                                        sm: "row",
                                      },
                                    }}
                                  >
                                    <Box sx={{ width: "100%" }}>
                                      {/*<Typography
                                        sx={{
                                          color: "rgb(0, 36, 101)",
                                          display: "block",
                                          fontFamily: "Nunito, sans-serif",
                                          fontSize: "16px",
                                          fontWeight: 700,
                                          mb: 1,
                                        }}
                                      >
                                        {t(
                                          "parent-subscription.title.paymentOn",
                                        )}{" "}
                                        {fmt(h?.startDate)}
                                      </Typography>*/}

                                      {!isBoost ? (
                                        <Typography
                                          sx={{
                                            color: "rgb(0, 36, 101)",
                                            display: "block",
                                            fontFamily: "Nunito, sans-serif",
                                            fontSize: "16px",
                                            fontWeight: 700,
                                            mb: 1,
                                          }}
                                        >
                                          {t(
                                            "parent-subscription.title.paymentOn",
                                          )}{" "}
                                          {fmt(h?.startDate)}
                                        </Typography>
                                      ) : (
                                        <><Typography
                                          sx={{
                                            color: "rgb(0, 36, 101)",
                                            display: "block",
                                            fontFamily: "Nunito, sans-serif",
                                            fontSize: "16px",
                                            fontWeight: 700,
                                            mb: 1,
                                          }}
                                        >
                                          ⚡{" "}
                                          {t(
                                            "parent-subscription.title.boostActiveOn",
                                          )}{" "}
                                          {fmt(isBoostDate)}
                                        </Typography><Typography
                                            sx={{
                                                color: "rgb(0, 36, 101)",
                                                display: "block",
                                                fontFamily: "Nunito, sans-serif",
                                                fontSize: "16px",
                                                fontWeight: 700,
                                                mb: 1,
                                            }}
                                        >
                                            {t(
                                                "parent-subscription.title.paymentOn",
                                            )}{" "}
                                            {fmt(h?.startDate)}
                                        </Typography></>
                                      )}

                                      <Typography
                                        sx={{
                                          fontSize: 12.5,
                                          color: "#111",
                                          mb: 0.8,
                                        }}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            color: "rgb(0, 36, 101)",
                                            display: "inline",
                                            fontFamily: "Nunito, sans-serif",
                                            fontSize: "13px",
                                            fontWeight: 700,
                                          }}
                                        >
                                          {t(
                                            "parent-subscription.title.amount",
                                          )}
                                          :
                                        </Box>{" "}
                                        {historyAmount}
                                      </Typography>

                                      <Typography
                                        sx={{ mb: historyLessons ? 0.8 : 0 }}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            color: statusColor,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {titleIcon}
                                        </Box>{" "}
                                        <Box
                                          component="span"
                                          sx={{
                                            color: statusColor,
                                            fontFamily: "Nunito, sans-serif",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {t(
                                            "parent-subscription.title.status",
                                          )}
                                          :
                                        </Box>{" "}
                                        <Box
                                          component="span"
                                          sx={{
                                            color: statusColor,
                                            fontFamily: "Nunito, sans-serif",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {paid
                                            ? t(
                                                "parent-subscription.title.paid",
                                              )
                                            : t(
                                                "parent-subscription.title.failed",
                                              )}
                                        </Box>
                                      </Typography>

                                      {historyLessons  ? (
                                        <Typography>
                                          <Box
                                            component="span"
                                            sx={{
                                              color: "rgb(0, 36, 101)",
                                              display: "inline",
                                              fontFamily: "Nunito, sans-serif",
                                              fontSize: "13px",
                                              fontWeight: 700,
                                            }}
                                          >
                                            {t(
                                              "parent-subscription.title.lessonUsed",
                                            )}
                                            :
                                          </Box>{" "}
                                          {historyLessons}
                                        </Typography>
                                      ) : null}
                                    </Box>

                                    {!paid ? (
                                      <Button
                                        variant="contained"
                                        disableElevation
                                        fullWidth={true}
                                        sx={{
                                          borderRadius: "8px",
                                          textTransform: "none",
                                          fontFamily: "Nunito, sans-serif",
                                          fontSize: "14px",
                                          fontWeight: 700,
                                          px: 3,
                                          height: 36,
                                          bgcolor: "rgb(244,67,54)",
                                          "&:hover": { bgcolor: "#DC2626" },
                                          width: { xs: "100%", sm: "auto" },
                                          alignSelf: {
                                            xs: "stretch",
                                            sm: "flex-start",
                                          },
                                        }}
                                        onClick={() =>
                                          setSelectedSubscription(h)
                                        }
                                      >
                                        {t("parent-subscription.title.pay")}
                                      </Button>
                                    ) : null}
                                  </Box>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    </Paper>
                  );
                })}

                {!isSLoading && keySubs.length === 0 ? (
                  <Div sx={{ py: { lg: 1 } }}>
                    <Typography aria-haspopup="true">
                      {t("parent-subscription.title.noSubscription")}
                    </Typography>
                  </Div>
                ) : null}
              </List>
            </TabPanel>

            {/* ---------------------- TAB 1: PLANS ---------------------- */}
            <TabPanel value={value} index={1} dir={theme.direction}>
              <PricingPlan />
            </TabPanel>
          </SwipeableViews>
        </Div>
      </CardContent>

      {/* ----------- BOOST INFO MODAL ------ */}
      <BoostInfoModal
        open={boostInfoOpen}
        onClose={() => setBoostInfoOpen(false)}
        t={t}
      />

      {/* ---------------------- CANCEL DIALOG ---------------------- */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{t("parent-subscription.title.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("parent-subscription.title.deleteContent")}
          </Typography>

          <TextField
            sx={{ mb: 2 }}
            label={t("parent-subscription.title.reason")}
            fullWidth
            margin="normal"
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              setErrCancel("");
            }}
          />

          <Stack sx={{ width: "100%", pl: 1, mt: 2 }} spacing={2}>
            {errCancel ? <Alert severity="error">{errCancel}</Alert> : null}
          </Stack>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 4,
            }}
          >
            <Button
              onClick={() => {
                setDialogOpen(false);
                setCancelReason("");
                setErrCancel("");
              }}
              color="primary"
              sx={{ mr: 2 }}
            >
              {t("common.noBtn")}
            </Button>
            <Button
              onClick={handleDialogConfirm}
              color="primary"
              disabled={isCLoading}
            >
              {t("common.yesBtn")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FullWidthTabs;
